import {FilterIcon, SearchIcon, TriangleDownIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, Button, IconButton, TextInput} from '@primer/react'
import {clsx} from 'clsx'
import styles from './CommentsFilter.module.css'

import {GitHubAvatar} from '@github-ui/github-avatar'
import {FeatureFlags} from '@primer/react/experimental'
import type {AuthorFilterItem} from './utils/get-author-filters'
import type {CommentsFilterState} from './use-comments-filter-state'

interface CommentsFilterProps {
  authorList: AuthorFilterItem[]
  className?: string
  filterState: CommentsFilterState
  onFilterStateChange: (filterState: CommentsFilterState) => void
  hideReviewThreadFilters: boolean
  iconOnly?: boolean
}

export function CommentsFilter({
  authorList,
  className,
  filterState,
  onFilterStateChange,
  hideReviewThreadFilters,
  iconOnly = false,
}: CommentsFilterProps) {
  const updateFilterText = (filterText: string) => {
    onFilterStateChange({...filterState, filterText})
  }

  const toggleResolvedFilter = () => {
    onFilterStateChange({...filterState, hideResolvedThreads: !filterState.hideResolvedThreads})
  }

  const toggleOutdatedFilter = () => {
    onFilterStateChange({...filterState, hideOutdatedThreads: !filterState.hideOutdatedThreads})
  }

  const onSelectAuthor = (authorLogin: string) => {
    if (filterState.selectedAuthor === authorLogin) {
      return onFilterStateChange({...filterState, selectedAuthor: undefined})
    }

    onFilterStateChange({...filterState, selectedAuthor: authorLogin})
  }

  const hasTextFilter = filterState.filterText.trim().length > 0
  const hasAuthorFilter = !!filterState.selectedAuthor
  const hasReviewFilter = filterState.hideResolvedThreads || filterState.hideOutdatedThreads

  // The conversation panel doesn't expose resolved/outdated toggles, so those values
  // remain at their defaults and should not trigger the indicator.
  const isFilteringActive = hasTextFilter || hasAuthorFilter || (!hideReviewThreadFilters && hasReviewFilter)

  return (
    <div className={clsx('d-flex flex-row gap-2 flex-nowrap', className)}>
      <TextInput
        block
        aria-label="Filter comments"
        leadingVisual={SearchIcon}
        placeholder="Filter comments"
        value={filterState.filterText}
        onChange={event => updateFilterText(event.target.value)}
      />
      {!hideReviewThreadFilters || authorList.length > 1 ? (
        <FeatureFlags flags={{primer_react_css_anchor_positioning: true}}>
          <div className="position-relative">
            <ActionMenu>
              <ActionMenu.Anchor>
                {iconOnly ? (
                  <IconButton
                    icon={FilterIcon}
                    aria-label={
                      isFilteringActive ? 'Additional comment filters (filters applied)' : 'Additional comment filters'
                    }
                  />
                ) : (
                  <Button
                    aria-label={
                      isFilteringActive ? 'Additional comment filters (filters applied)' : 'Additional comment filters'
                    }
                    className="flex-shrink-0"
                    leadingVisual={FilterIcon}
                    trailingAction={TriangleDownIcon}
                  >
                    Filter
                  </Button>
                )}
              </ActionMenu.Anchor>
              <ActionMenu.Overlay width="small">
                <ActionList selectionVariant="single">
                  {!hideReviewThreadFilters && (
                    <>
                      <ActionList.Item
                        selected={filterState.hideResolvedThreads}
                        onSelect={() => toggleResolvedFilter()}
                      >
                        Hide resolved comments
                      </ActionList.Item>
                      <ActionList.Item
                        selected={filterState.hideOutdatedThreads}
                        onSelect={() => toggleOutdatedFilter()}
                      >
                        Hide outdated comments
                      </ActionList.Item>
                    </>
                  )}
                  {authorList.length > 1 && (
                    <>
                      {!hideReviewThreadFilters && <ActionList.Divider />}
                      <ActionList.Group>
                        <ActionList.GroupHeading>Filter by</ActionList.GroupHeading>

                        {authorList.map(author => (
                          <ActionList.Item
                            key={author.login}
                            selected={filterState.selectedAuthor === author.login}
                            onSelect={() => onSelectAuthor(author.login)}
                          >
                            <ActionList.LeadingVisual>
                              <GitHubAvatar src={author.avatarUrl} />
                            </ActionList.LeadingVisual>
                            {author.login}
                          </ActionList.Item>
                        ))}
                      </ActionList.Group>
                    </>
                  )}
                </ActionList>
              </ActionMenu.Overlay>
            </ActionMenu>
            {isFilteringActive && <span className={styles.filterIndicator} aria-hidden="true" />}
          </div>
        </FeatureFlags>
      ) : null}
    </div>
  )
}
